import { ASPECT_RATIO_TOLERANCE } from '@/app/constants';
import type { SubsPlanType } from '@/app/types';

/**
 * 将数值对齐到指定步长
 * @param value 原始数值
 * @param min 最小值
 * @param max 最大值
 * @param step 步长
 * @returns 对齐后的数值
 */
export const snapToStep = (value: number, min: number, max: number, step: number): number => {
  // 如果步长无效，直接返回原值
  if (step <= 0) return value;

  // 计算相对于最小值的偏移量
  const offset = value - min;
  // 计算最接近的步数（四舍五入）
  const stepCount = Math.round(offset / step);
  // 计算对齐后的值
  const snappedValue = min + stepCount * step;

  // 确保结果在有效范围内
  return Math.max(min, Math.min(max, snappedValue));
};

/**
 * 根据数值计算在滑块上的位置百分比
 * @param value 当前数值
 * @param min 最小值
 * @param max 最大值
 * @returns 位置百分比 (0-100)
 */
export const getPositionFromValue = (value: number, min: number, max: number): number => {
  return ((value - min) / (max - min)) * 100;
};

/**
 * 获取距离点击位置最近的滑块手柄
 * @param clickValue 点击位置对应的数值
 * @param leftValue 左侧手柄的数值
 * @param rightValue 右侧手柄的数值
 * @returns 0 表示左侧手柄，1 表示右侧手柄
 */
export const getClosestThumb = (clickValue: number, leftValue: number, rightValue: number): 0 | 1 => {
  // 计算点击位置到两个手柄的距离
  const leftDistance = Math.abs(clickValue - leftValue);
  const rightDistance = Math.abs(clickValue - rightValue);
  // 返回距离更近的手柄
  return leftDistance < rightDistance ? 0 : 1;
};

/**
 * 根据鼠标位置计算对应的数值
 * @param clientX 鼠标的 X 坐标
 * @param sliderElement 滑块 DOM 元素
 * @param min 最小值
 * @param max 最大值
 * @returns 对应的数值
 */
export const getValueFromPosition = (clientX: number, sliderElement: HTMLElement, min: number, max: number): number => {
  // 获取滑块元素的边界信息
  const rect = sliderElement.getBoundingClientRect();
  // 计算鼠标在滑块上的百分比位置，确保在 0-1 范围内
  const percentage = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  // 根据百分比计算对应的数值
  return min + percentage * (max - min);
};

/**
 * 验证并修正滑块的左右值
 * @param params 包含左右值、边界值、最小间隔和步长的对象
 * @returns 修正后的 [左值, 右值] 或 null（如果无法修正）
 */
export const validateAndFixValues = ({
  leftValue,
  rightValue,
  min,
  max,
  minGap,
  step,
}: {
  leftValue: number;
  rightValue: number;
  min: number;
  max: number;
  minGap: number;
  step: number;
}): [number, number] | null => {
  // 应用步进对齐并确保值在边界内
  const snappedLeft = snapToStep(leftValue, min, max, step);
  const snappedRight = snapToStep(rightValue, min, max, step);

  // 验证值的有效性：左值小于右值且间隔满足最小要求
  if (snappedLeft < snappedRight && snappedRight - snappedLeft >= minGap) {
    return [snappedLeft, snappedRight];
  }

  return null;
};

/**
 * 计算范围拖拽时的新位置
 * @param deltaValue 拖拽的偏移量
 * @param initialLeft 初始左值
 * @param initialRight 初始右值
 * @param min 最小边界值
 * @param max 最大边界值
 * @returns 新的 [左值, 右值]
 */
export const calculateRangeDragPosition = (
  deltaValue: number,
  initialLeft: number,
  initialRight: number,
  min: number,
  max: number,
): [number, number] => {
  // 计算拖拽后的新位置
  let newLeft = initialLeft + deltaValue;
  let newRight = initialRight + deltaValue;

  // 确保范围不超出边界，同时保持原有的间隔宽度
  const rangeWidth = newRight - newLeft;

  // 如果左边界超出最小值，调整整个范围
  if (newLeft < min) {
    newLeft = min;
    newRight = newLeft + rangeWidth;
  }

  // 如果右边界超出最大值，调整整个范围
  if (newRight > max) {
    newRight = max;
    newLeft = newRight - rangeWidth;
  }

  return [newLeft, newRight];
};
/**
 * 获取用户扩展时长限制（秒）
 * @param subsPlan 用户订阅计划类型
 * @returns 对应计划的扩展时长限制
 */
export const getUserExtendLimit = (subsPlan: SubsPlanType): number => {
  return {
    free: 30,
    standard_v2: 60,
    premium_v2: 180,
    ultimate_v2: 300,
    team_v2: 300,
  }[subsPlan];
};

/**
 * 检查两张图片的宽高比是否一致
 * @param ratios 包含两个图片宽高比的元组 [ratio1, ratio2]
 * @returns 如果宽高比差异在容差范围内返回 true，否则返回 false
 */
export const checkImageRatiosByRatios = ([ratio1, ratio2]: [number, number]): boolean => {
  return !(ratio1 / ratio2 > ASPECT_RATIO_TOLERANCE || ratio2 / ratio1 > ASPECT_RATIO_TOLERANCE);
};
